<template>
  <AreaPageContainer
    v-if="area"
  >
      <!-- Content wrapper with header and footer -->
      <AreaPageBody>
        <AreaHeader />

        <!-- Inner Content -->
        <div class="tw-p-10">
          <!-- Title -->
          <div>
            <div class="tw-flex tw-justify-between">
              <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">
                Statistics
              </h2>
            </div>
            <AdvisorCoreStripes />
          </div>

          <!-- Text -->
          <p class="tw-font-poppins tw-mt-10">
            {{ area.headerText }}
          </p>

          <!-- Table -->
          <div class="tw-mt-10">
            <table class="tw-mt-10 tw-w-full tw-border-collapse tw-border tw-border-gray-200">
              <thead>
                <tr class="tw-bg-gray-100">
                  <th class="tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-w-10">
                    <button 
                      @click="toggleEdit" 
                      class="tw-bg-transparent tw-border-none tw-cursor-pointer"
                    >
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                  </th>
                  <th class="tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-font-semibold">Statistic</th>
                  <th
                    v-for="header in visiblePortfolioNames"
                    :key="header"
                    class="tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-font-semibold"
                  >
                    {{ header }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Portfolio Overview Section -->
                <template v-if="!hiddenSections.includes('overview')">
                  <tr class="tw-bg-gray-50 tw-font-medium tw-text-gray-700">
                    <td class="tw-py-2 tw-px-4">
                      <button 
                        v-if="isEditing"
                        @click="removeSection('overview')"
                        class="tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                    <td colspan="99" class="tw-py-2 tw-px-4">Portfolio Overview</td>
                  </tr>
                  <template v-for="label in overviewLabels">
                    <tr 
                      v-if="!hiddenLabels.includes(label)" 
                      :key="label"
                    >
                      <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                        <button 
                          v-if="isEditing"
                          @click="removeRow(label)"
                          class="tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </td>
                      <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">{{ label }}</td>
                      <td
                        v-for="portfolioName in visiblePortfolioNames"
                        :key="portfolioName + '-' + label"
                        class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                        @click="handleModalClick(label, portfolioName)"
                      >
                        {{ getValue(portfolioName, label) || '--' }}
                      </td>
                    </tr>
                  </template>
                </template>

                <!-- Performance Metrics Section -->
                <template v-if="!hiddenSections.includes('performance')">
                  <tr class="tw-bg-gray-50 tw-font-medium tw-text-gray-700">
                    <td class="tw-py-2 tw-px-4">
                      <button 
                        v-if="isEditing"
                        @click="removeSection('performance')"
                        class="tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer"
                      >
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                    <td colspan="99" class="tw-py-2 tw-px-4">Performance Metrics</td>
                  </tr>
                  <template v-for="label in performanceLabels">
                    <tr 
                      v-if="!hiddenLabels.includes(label)" 
                      :key="label"
                    >
                      <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                        <button 
                          v-if="isEditing"
                          @click="removeRow(label)"
                          class="tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </td>
                      <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">{{ label }}</td>
                      <td
                        v-for="portfolioName in visiblePortfolioNames"
                        :key="portfolioName + '-' + label"
                        class="tw-border tw-border-gray-200 tw-py-2 tw-px-4"
                        @click="handleModalClick(label, portfolioName)"
                      >
                        {{ getValue(portfolioName, label) || '--' }}
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>

            <ChartClickthroughs
              :click-data="chartClickData"
              :get-table-data="area.clickthroughFunction"
            />
          </div>

          <!-- Disclaimer and Methodology -->
          <div class="tw-flex tw-gap-10">
            <div class="tw-flex tw-flex-col tw-basis-0 tw-grow tw-py-10 tw-bg-white !tw-text-[#353a46]">
              <h3 class="tw-text-xl tw-font-bold tw-font-georgia">
                Methodology
              </h3>
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="short"
                class="tw-text-xs tw-font-poppins"
              />
              <CheckupMethodologyWrapper
                :component-name="area.methodology"
                display-type="long"
                class="tw-text-xs tw-font-poppins tw-hidden"
              />
            </div>
          </div>
        </div>

        <AreaFooter />
        <CheckupDisclaimer class="tw-mt-16">
          <CheckupMethodologyWrapper
            :component-name="area.methodology"
            display-type="long"
          />
        </CheckupDisclaimer>
      </AreaPageBody>

      <!-- Settings Sidebar -->
      <AreaSidebar
        :area="area"
      >
        <IncludeInPresentationToggle :name="area?.name" />
        <template #other>
          <SpeakersNotes :notes="area?.explanation" />
        </template>
        <!-- <AIChat /> -->
      </AreaSidebar>
  </AreaPageContainer>

  <div v-else>
    <p>Loading...</p>
  </div>
</template>

<script>
import { GRADE_COLORS } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import { useChatStore } from '@frontend/ui/stores/chatStore';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { mapStores } from 'pinia';

import AreaPageContainer from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageContainer.vue';
import AreaPageBody from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageBody.vue';
import ChartWrapper from '@frontend/ui/components/charts/ChartWrapper.vue';
import ChartClickthroughs from '@frontend/ui/features/checkup/components/ChartClickthroughs.vue';
import CheckupDisclaimer from '@frontend/ui/features/checkup/components/CheckupDisclaimer.vue';
import StatisticsMethodology from '@frontend/ui/features/checkup/components/methodologies/StatisticsMethodology.vue';

import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import ChartToggle from '@frontend/ui/features/checkup/components/sidebar/ChartToggle.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import CheckupMethodologyWrapper from '@frontend/ui/features/checkup/components/methodologies/CheckupMethodologyWrapper.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';

export default {
  name: 'StatisticsArea',
  components: {
    AreaPageContainer,
    AreaPageBody,
    ChartWrapper,
    ChartClickthroughs,
    CheckupDisclaimer,
    StatisticsMethodology,
    HighChartsWrapper,
    AreaSidebar,
    AdvisorCoreStripes,
    IncludeInPresentationToggle,
    SpeakersNotes,
    ChartToggle,
    AIChat,
    AreaHeader,
    CheckupMethodologyWrapper,
    AreaFooter,
  },
  props: {
    isActiveTab: Boolean,
  },
  data() {
    return {
      expanded: false,
      GRADE_COLORS,
      chartClickData: null,
      isEditing: false,
      hiddenLabels: [],
      hiddenSections: [],
      overviewLabels: [
        'Turnover',
        'Expense Ratio',
        'Yield',
        'Equity Dividend Yield',
        'Fixed Income YTM',
        'Debt to Equity',
        'Forward PE Ratio',
        'Avg # Hldgs (funds)'
      ],
      performanceLabels: [
        '3 Yr Mean Returns',
        '3 Yr Standard Deviation',
        '5 Yr Mean Returns',
        '5 Yr Standard Deviation',
        '10 Yr Mean Returns',
        '10 Yr Standard Deviation'
      ]
    };
  },
  computed: {
    ...mapStores(useChatStore, useCheckupStore),

    area() {
      return this.checkupStore.areasByName?.['Statistics'];
    },
    benchmarkName() {
      const name = this.checkupStore.benchmark?.name;
      return name || 'Benchmark';
    },
    uniquePortfolioNames() {
      return [...new Set(this.area?.extraData.map(data => data.name))];
    },
    uniqueLabels() {
      return [...new Set(this.area?.extraData.map(data => data.label))];
    },
    visibleLabels() {
      return this.uniqueLabels.filter(label => !this.hiddenLabels.includes(label));
    },
    visiblePortfolioNames() {
      return this.uniquePortfolioNames;
    },
  },

  watch: {
    async isActiveTab() {
      if (this.isActiveTab) {
        const { name, current, proposal, grade } = this.area;

        await this.chatStore.setContext({
          jsonData: {
            currentArea: {
              name,
              [this.checkupStore.portfolio.name]: current,
              [this.benchmarkName]: proposal,
              magnitudeOfDifference: grade,
              holdingsConcentrationBreakdown: name === 'Concentration Breakdown' ? this.checkupStore.portfolio.sector_market_cap_asset_class_breakdown?.['concentration_breakdown'] : undefined,
            },
            portfolio: {
              name: this.checkupStore.portfolio.name,
              holdings: this.checkupStore.portfolio.holdings.map(h => this.createContextFromHolding(h)),
            },
            benchmark: {
              name: this.benchmarkName,
              holdings: this.checkupStore.benchmark.holdings.map(h => this.createContextFromHolding(h)),
            },
          },
        });
      }
    },
  },
  methods: {
    getValue(portfolioName, label) {
      const data = this.area?.extraData.find(
        item => item.name === portfolioName && item.label === label
      );
      return data?.value || null;
    },
    makeValuesPercentages(obj) {
      if (!obj) return {};
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[key] = `${((value || 0) * 100).toFixed(2)}%`;
        return acc;
      }, {});
    },
    createContextFromHolding(h) {
      return {
        symbol: h.ticker.symbol,
        dollars: h.dollars,
        returns: this.makeValuesPercentages(h.ticker.trailing_returns_monthly_rebalance),
        sectors: this.makeValuesPercentages(h.ticker.security_breakdown?.['Breakdown']),
        expenseRatio: `${(h.ticker.expense_ratio * 100).toFixed(2)}%`,
        assetClasses: this.makeValuesPercentages(h.ticker.security_breakdown?.['AssetType']),
        marketCap: this.makeValuesPercentages(h.ticker.security_breakdown?.['Market Cap']),
        trailingVolatilities: this.makeValuesPercentages(h.ticker.trailing_volatilities),
      };
    },
    handleModalClick(rowName, portfolioName) {
      this.chartClickData = {
        field: rowName,
        label: portfolioName,
      };
    },
    toggleEdit() {
      this.isEditing = !this.isEditing;
    },
    removeRow(label) {
      this.hiddenLabels.push(label);
      this.updateVisibleLabels();
    },
    removeSection(section) {
      this.hiddenSections.push(section);
      const labelsToHide = section === 'overview' ? this.overviewLabels : this.performanceLabels;
      this.hiddenLabels.push(...labelsToHide);
      this.updateVisibleLabels();
    },
    updateVisibleLabels() {
      // Update the area with current visible labels
      this.checkupStore.updateArea('Statistics', {
        ...this.area,
        visibleLabels: this.uniqueLabels.filter(label => !this.hiddenLabels.includes(label))
      });
    }
  },
};
</script>

<style scoped>
button {
  cursor: pointer;
}

.tw-w-10 {
  width: 40px;
}
</style>
